import { useLayoutEffect, useRef } from 'react';

/** Elements a Tab press can land on (before visibility and tabindex checks). */
const TABBABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [contenteditable=""], [contenteditable="true"], [tabindex]';

function isTabbable(el: HTMLElement): boolean {
  if (el.tabIndex < 0) return false;
  if (el.closest('[inert], [aria-hidden="true"]')) return false;
  // Rendered: display none (or inside it) has no client rects.
  return el.getClientRects().length > 0;
}

/**
 * Move focus to the first tabbable element after `container` (dir 1) or the last one before it
 * (dir -1), skipping everything inside it. The way out of a widget that keeps Tab for itself
 * (a terminal). Returns whether focus moved.
 */
export function focusOutside(container: HTMLElement, dir: 1 | -1): boolean {
  const all = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE)).filter((el) => !container.contains(el) && isTabbable(el));
  const after = (el: HTMLElement) => !!(container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
  const target = dir === 1 ? all.find(after) : all.filter((el) => !after(el)).at(-1);
  if (!target) return false;
  target.focus();
  return document.activeElement === target;
}

/** Where focus was when an overlay opened, and how to find that element again if its row re-rendered. */
export interface FocusReturn {
  el: HTMLElement;
  selectors: string[];
}

/** The return point of each open overlay, by its content element, so one opened from another inherits it. */
const RETURNS = new WeakMap<Element, FocusReturn>();
/** The return point of the overlay that closed last, for one that opens right after it. */
let handedOver: { r: FocusReturn; at: number } | null = null;
const HAND_OVER_MS = 1000;

function pointAt(el: HTMLElement): FocusReturn {
  const selectors: string[] = [];
  const esc = (v: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&'));
  for (const a of Array.from(el.attributes)) {
    if (a.name.startsWith('data-') && a.value && !a.name.startsWith('data-state')) selectors.push(`${el.tagName.toLowerCase()}[${a.name}="${esc(a.value)}"]`);
  }
  if (el.id) selectors.push(`#${esc(el.id)}`);
  const label = el.getAttribute('aria-label');
  if (label) selectors.push(`${el.tagName.toLowerCase()}[aria-label="${esc(label)}"]`);
  return { el, selectors };
}

/**
 * Where focus should go back to when an overlay that opens now closes. The focused element
 * itself, except: inside a menu, the button that opened the menu (the item is gone by then);
 * inside another dialog (the palette, a dialog that hands over to the next), where that one
 * would have returned. Null when focus is on the page itself.
 */
export function returnPoint(active: Element | null): FocusReturn | null {
  if (!(active instanceof HTMLElement) || active === document.body) return null;
  const overlay = active.closest('[role="dialog"], [role="alertdialog"]');
  const inherited = overlay ? RETURNS.get(overlay) : undefined;
  if (inherited) return inherited;
  const menu = active.closest('[role="menu"]');
  const trigger = menu?.getAttribute('aria-labelledby') ? document.getElementById(menu.getAttribute('aria-labelledby')!) : null;
  return pointAt(trigger ?? active);
}

/** Focus the element an overlay opened from, or the same row re-rendered; false when neither is there. */
export function restoreFocus(r: FocusReturn | null): boolean {
  if (!r) return false;
  let target: HTMLElement | null = r.el.isConnected ? r.el : null;
  for (const sel of r.selectors) {
    if (target) break;
    try {
      target = document.querySelector<HTMLElement>(sel);
    } catch {
      /* a selector the browser does not take */
    }
  }
  if (!target) return false;
  target.focus({ preventScroll: false });
  return document.activeElement === target;
}

/**
 * Focus return for an overlay opened from state (a URL parameter, a store flag, a menu item),
 * which has no Radix trigger to go back to (WCAG 2.4.3). Remembers what had focus when `open`
 * turned true and gives it back on close; spread the handlers on the Radix content.
 */
export function useFocusReturn(open: boolean): { onOpenAutoFocus: (e: Event) => void; onCloseAutoFocus: (e: Event) => void } {
  const back = useRef<FocusReturn | null>(null);
  useLayoutEffect(() => {
    if (open) {
      // Focus on the page itself right after another overlay closed (the palette ran a command
      // that opens this dialog): go back to where that one would have.
      const handOver = handedOver && performance.now() - handedOver.at < HAND_OVER_MS ? handedOver.r : null;
      back.current = returnPoint(document.activeElement) ?? handOver;
      return;
    }
    if (back.current) handedOver = { r: back.current, at: performance.now() };
  }, [open]);
  return {
    onOpenAutoFocus: (e) => {
      if (e.currentTarget instanceof Element && back.current) RETURNS.set(e.currentTarget, back.current);
    },
    onCloseAutoFocus: (e) => {
      if (e.defaultPrevented) return;
      const r = back.current;
      back.current = null;
      // Something moved focus on purpose meanwhile (a command run from the palette, the next
      // dialog): leave it there rather than pull it back.
      const active = document.activeElement;
      const host = e.currentTarget instanceof Element ? e.currentTarget : null;
      if (active && active !== document.body && !host?.contains(active)) {
        e.preventDefault();
        return;
      }
      if (restoreFocus(r)) e.preventDefault();
    },
  };
}
